import React, { useState } from 'react';
import { TreeDeciduous, AlertTriangle, Award, Shield, Leaf, Cloud, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tree } from '../../types/tree';
import './SpeciesCard.css';

interface SpeciesCardProps {
  species: string;
  data: {
    count: number;
    family: string;
    native: boolean;
    endemic: boolean;
    invasive: boolean;
    avgGBH: number;
    avgHeight: number;
    avgBiomass: number;
    avgCarbon: number;
    avgCO2: number;
    sampleTree: Tree;
  };
  delay?: number;
}

export const SpeciesCard: React.FC<SpeciesCardProps> = ({ species, data, delay = 0 }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay,
        ease: "easeOut" as const,
      },
    },
  };

  const metricInfo = {
    biomass: "Total above-ground and below-ground dry weight of the tree, including trunk, branches, leaves and roots. This represents the physical mass of organic material.",
    carbon: "Amount of carbon stored in the tree's biomass. Trees absorb CO₂ from the atmosphere and store carbon as they grow.",
    co2: "CO₂ equivalent - the amount of carbon dioxide removed from the atmosphere and stored by this tree. This helps combat climate change."
  };

  const getConservationBadge = () => {
    if (data.invasive) {
      return { icon: <AlertTriangle size={16} />, text: 'Invasive', color: '#e74c3c' };
    }
    if (data.endemic) {
      return { icon: <Award size={16} />, text: 'Endemic', color: '#8e44ad' };
    }
    if (data.native) {
      return { icon: <Shield size={16} />, text: 'Native', color: '#27ae60' };
    }
    return { icon: <TreeDeciduous size={16} />, text: 'Non-native', color: '#3498db' };
  };

  const badge = getConservationBadge();

  return (
    <motion.div
      className="species-card"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -4, boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }}
      transition={{ duration: 0.2 }}
    >
      <div className="species-card__header">
        <div className="species-card__icon">
          <TreeDeciduous size={24} />
        </div>
        <div className="species-card__badge" style={{ background: badge.color }}>
          {badge.icon}
          <span>{badge.text}</span>
        </div>
      </div>

      <div className="species-card__content">
        <h3 className="species-card__name">{species}</h3>
        {data.sampleTree.Local_Name && (
          <p className="species-card__local-name">{data.sampleTree.Local_Name}</p>
        )}
        {data.sampleTree.Common_Name && (
          <p className="species-card__common-name">{data.sampleTree.Common_Name}</p>
        )}

        <div className="species-card__family">{data.family}</div>

        <div className="species-card__stats">
          <div className="species-card__stat">
            <div className="species-card__stat-value">{data.count}</div>
            <div className="species-card__stat-label">Trees</div>
          </div>
          <div className="species-card__stat">
            <div className="species-card__stat-value">{data.avgGBH.toFixed(0)}</div>
            <div className="species-card__stat-label">Avg GBH (cm)</div>
          </div>
          <div className="species-card__stat">
            <div className="species-card__stat-value">{data.avgHeight.toFixed(1)}</div>
            <div className="species-card__stat-label">Avg Height (m)</div>
          </div>
        </div>

        {data.avgBiomass > 0 && (
          <div className="species-card__biomass">
            <div className="species-card__biomass-header">
              <Leaf size={16} />
              <span>Average Tree Performance</span>
            </div>
            <div className="species-card__biomass-metrics">
              <div className="species-card__biomass-metric species-card__biomass-metric--with-tooltip">
                <div className="species-card__biomass-label">
                  Biomass
                  <span className="species-card__info-icon" aria-label="Information about Biomass">
                    <Info size={12} />
                  </span>
                </div>
                <div className="species-card__tooltip">
                  {metricInfo.biomass}
                </div>
                <div className="species-card__biomass-value">{data.avgBiomass.toFixed(3)} tonnes</div>
              </div>
              <div className="species-card__biomass-metric species-card__biomass-metric--with-tooltip">
                <div className="species-card__biomass-label">
                  Carbon
                  <span className="species-card__info-icon" aria-label="Information about Carbon">
                    <Info size={12} />
                  </span>
                </div>
                <div className="species-card__tooltip">
                  {metricInfo.carbon}
                </div>
                <div className="species-card__biomass-value">{data.avgCarbon.toFixed(3)} tonnes</div>
              </div>
              <div className="species-card__biomass-metric species-card__biomass-metric--co2 species-card__biomass-metric--with-tooltip">
                <Cloud size={14} />
                <div className="species-card__biomass-label species-card__biomass-label--co2">
                  CO₂e
                  <span className="species-card__info-icon" aria-label="Information about CO₂ equivalent">
                    <Info size={12} />
                  </span>
                </div>
                <div className="species-card__tooltip">
                  {metricInfo.co2}
                </div>
                <div className="species-card__biomass-value">{data.avgCO2.toFixed(3)} tonnes</div>
              </div>
            </div>
          </div>
        )}

        {data.sampleTree.Uses && (
          <div className="species-card__uses">
            <strong>Uses:</strong> {data.sampleTree.Uses}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SpeciesCard;
